import Konva from 'konva';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Image as KonvaImage, Layer, Line, Rect, Stage, Text } from 'react-konva';
import useImage from 'use-image';

import config from '@/config';

import './styles/BrushCrop.css';

const BrushCrop = forwardRef(
  ({ source, onBrushDataChange, brushStrokeWidth = 20 }, ref) => {
    // 1) Build the proxied image URL.
    const isFullURL =
      source.startsWith('http://') ||
      source.startsWith('https://') ||
      source.startsWith('blob:') ||
      source.startsWith('data:');

    const originalUrl = isFullURL
      ? source
      : `${config.app.imageServiceDomain}/${source}/brightness=0.8`;

    const proxiedUrl = isFullURL
      ? source
      : `${config.app.backendUrl}/api/image-proxy?url=${encodeURIComponent(originalUrl)}`;

    // 2) Load the image.
    const [loadedImage] = useImage(proxiedUrl, 'anonymous');
    useEffect(() => {
      if (loadedImage) {
        console.log('[BrushCrop] Loaded image:', {
          naturalWidth: loadedImage.naturalWidth,
          naturalHeight: loadedImage.naturalHeight,
        });
      } else {
        console.log('[BrushCrop] loadedImage is null or undefined');
      }
    }, [loadedImage]);

    // 3) Store brush strokes; each stroke is an object with a 'points' array.
    const [lines, setLines] = useState([]);
    const [isDrawing, setIsDrawing] = useState(false);

    // 4) Refs for the container and stage, plus state for display dimensions.
    const containerRef = useRef(null);
    const stageRef = useRef(null);
    const [imageDims, setImageDims] = useState({ width: 0, height: 0 });

    // 5) Scale the image to fit the container's width OR height, whichever is smaller.
    useEffect(() => {
      if (loadedImage && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const ratioX = containerRect.width / loadedImage.naturalWidth;
        const ratioY = containerRect.height / loadedImage.naturalHeight;
        const scale = Math.min(ratioX, ratioY);

        const width = loadedImage.naturalWidth * scale;
        const height = loadedImage.naturalHeight * scale;
        setImageDims({ width, height });
      }
    }, [loadedImage]);

    // Also update the image dimensions on window resize:
    useEffect(() => {
      const handleResize = () => {
        if (loadedImage && containerRef.current) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const ratioX = containerRect.width / loadedImage.naturalWidth;
          const ratioY = containerRect.height / loadedImage.naturalHeight;
          const scale = Math.min(ratioX, ratioY);

          const width = loadedImage.naturalWidth * scale;
          const height = loadedImage.naturalHeight * scale;
          setImageDims({ width, height });
        }
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [loadedImage]);

    // 6) Mouse/touch handlers for drawing brush strokes.
    const handleMouseDown = () => {
      if (!stageRef.current) return;
      setIsDrawing(true);
      const pos = stageRef.current.getPointerPosition();
      setLines((prev) => {
        const next = [...prev, { points: [pos.x, pos.y] }];
        onBrushDataChange && onBrushDataChange(next);
        return next;
      });
    };

    const handleMouseMove = () => {
      if (!isDrawing || !stageRef.current) return;
      const pos = stageRef.current.getPointerPosition();
      setLines((prevLines) => {
        const lastLine = prevLines[prevLines.length - 1];
        const newPoints = [...lastLine.points, pos.x, pos.y];
        const updatedLines = prevLines
          .slice(0, prevLines.length - 1)
          .concat({ points: newPoints });
        onBrushDataChange && onBrushDataChange(updatedLines);
        return updatedLines;
      });
    };

    const handleMouseUp = () => {
      setIsDrawing(false);
      onBrushDataChange && onBrushDataChange(lines);
    };

    // 7) getBrushImage: Capture the stage, then process it to apply a mask based on the brush strokes,
    //    and crop only the painted area (including the brush stroke width).
    const getBrushImage = () => {
      return new Promise((resolve, reject) => {
        const stage = stageRef.current;
        if (!stage) {
          reject(new Error('Stage not available'));
          return;
        }
        stage.batchDraw();

        let resolved = false;
        // 2-second fallback if toBlob never calls back.
        const timer = setTimeout(() => {
          if (!resolved) {
            try {
              const dataURL = stage.toDataURL({ pixelRatio: Konva.pixelRatio || 1 });
              resolved = true;
              processCapturedImage(dataURL).then(resolve).catch(reject);
            } catch (error) {
              resolved = true;
              reject(error);
            }
          }
        }, 2000);

        if (typeof stage.toBlob === 'function') {
          console.log('[BrushCrop] Calling toBlob...');
          stage.toBlob(
            (blob) => {
              if (!resolved) {
                clearTimeout(timer);
                resolved = true;
                if (blob) {
                  // Convert Blob to dataURL for processing.
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    processCapturedImage(reader.result).then(resolve).catch(reject);
                  };
                  reader.readAsDataURL(blob);
                } else {
                  console.error('[BrushCrop] Blob is null in toBlob callback');
                  reject(new Error('Failed to capture brush image'));
                }
              }
            },
            'image/png'
          );
        } else {
          try {
            const dataURL = stage.toDataURL({ pixelRatio: Konva.pixelRatio || 1 });
            resolved = true;
            processCapturedImage(dataURL).then(resolve).catch(reject);
          } catch (error) {
            resolved = true;
            reject(error);
          }
        }
      });
    };

    // 8) processCapturedImage: Process the captured stage image by:
    //    a) Converting pointer coordinates from display to natural coordinates.
    //    b) Computing a bounding box that encompasses all brush strokes,
    //       expanding each point by half the brush stroke width.
    //    c) Drawing the high-res image on an offscreen canvas.
    //    d) Creating a mask canvas where we "stroke" each brush stroke path (using the brush width).
    //    e) Applying the mask and cropping the image to the computed bounding box.
    const processCapturedImage = (dataURL) => {
      return new Promise((resolve, reject) => {
        // If there are no brush strokes, return the full image capture.
        if (!lines.length) {
          console.warn('[BrushCrop] No brush strokes; returning full image capture.');
          resolve(dataURLtoBlob(dataURL));
          return;
        }
        if (!loadedImage) {
          reject(new Error('Image not loaded'));
          return;
        }

        // Compute conversion factor: natural image dimensions vs. display dimensions.
        const conversionFactor = loadedImage.naturalWidth / imageDims.width;

        // Compute unified bounding box across all strokes, taking into account the brush stroke width.
        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;
        lines.forEach((line) => {
          const { points } = line;
          for (let i = 0; i < points.length; i += 2) {
            // Each point is the center of the brush stroke.
            const naturalX = points[i] * conversionFactor;
            const naturalY = points[i + 1] * conversionFactor;
            // Expand the point by half the brushStrokeWidth (in natural coordinates).
            const halfStroke = (brushStrokeWidth * conversionFactor) / 2;
            if (naturalX - halfStroke < minX) minX = naturalX - halfStroke;
            if (naturalX + halfStroke > maxX) maxX = naturalX + halfStroke;
            if (naturalY - halfStroke < minY) minY = naturalY - halfStroke;
            if (naturalY + halfStroke > maxY) maxY = naturalY + halfStroke;
          }
        });
        // (Optional) Apply additional padding if desired.
        const padding = 0; // Set to 0 if you want exactly the brush area.
        minX = Math.max(0, minX - padding);
        minY = Math.max(0, minY - padding);
        maxX = Math.min(loadedImage.naturalWidth, maxX + padding);
        maxY = Math.min(loadedImage.naturalHeight, maxY + padding);
        const cropWidth = maxX - minX;
        const cropHeight = maxY - minY;
        console.log('[BrushCrop] Unified bounding box (natural coordinates):', {
          minX,
          minY,
          cropWidth,
          cropHeight,
        });
        if (cropWidth <= 0 || cropHeight <= 0) {
          reject(new Error('Invalid bounding box from strokes'));
          return;
        }

        // Create an offscreen canvas at natural resolution and draw the high-res image.
        const fullCanvas = document.createElement('canvas');
        fullCanvas.width = loadedImage.naturalWidth;
        fullCanvas.height = loadedImage.naturalHeight;
        const fullCtx = fullCanvas.getContext('2d');
        fullCtx.clearRect(0, 0, fullCanvas.width, fullCanvas.height);
        fullCtx.drawImage(loadedImage, 0, 0);

        // Create a mask canvas at natural resolution.
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = loadedImage.naturalWidth;
        maskCanvas.height = loadedImage.naturalHeight;
        const maskCtx = maskCanvas.getContext('2d');
        maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
        // Set up the stroke style to mimic the brush drawing.
        maskCtx.strokeStyle = '#fff';
        maskCtx.lineWidth = brushStrokeWidth * conversionFactor;
        maskCtx.lineCap = 'round';
        maskCtx.lineJoin = 'round';

        // For each stroke, "stroke" the path (instead of closing and filling it)
        // so that only the painted line (with proper thickness) is used as a mask.
        lines.forEach((line) => {
          const { points } = line;
          if (points.length >= 2) {
            maskCtx.beginPath();
            maskCtx.moveTo(points[0] * conversionFactor, points[1] * conversionFactor);
            for (let i = 2; i < points.length; i += 2) {
              maskCtx.lineTo(points[i] * conversionFactor, points[i + 1] * conversionFactor);
            }
            maskCtx.stroke();
          }
        });

        // Apply the mask: using 'destination-in' keeps only the pixels
        // where the mask (i.e. the brush strokes) is drawn.
        fullCtx.globalCompositeOperation = 'destination-in';
        fullCtx.drawImage(maskCanvas, 0, 0);

        // Crop the full image canvas to the computed bounding box.
        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = cropWidth;
        cropCanvas.height = cropHeight;
        const cropCtx = cropCanvas.getContext('2d');
        cropCtx.drawImage(fullCanvas, -minX, -minY);

        // Convert the cropped canvas to a Blob.
        cropCanvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to crop brush image'));
          }
        }, 'image/png');
      });
    };

    // Helper: Convert a dataURL to a Blob.
    function dataURLtoBlob(dataURL) {
      const byteString = atob(dataURL.split(',')[1]);
      const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], { type: mimeString });
    }

    // 9) Expose getBrushImage via the forwarded ref.
    useImperativeHandle(ref, () => ({
      getBrushImage,
    }));

    // 10) Render the Stage with two layers:
    //     - The background layer shows the loaded image.
    //     - The overlay layer displays a tinted rectangle and the brush strokes.
    return (
      <div className="brush-crop">
        {/* We reuse the reactEasyCrop_Container class so it matches your circle/rect container. */}
        <div className="reactEasyCrop_Container" ref={containerRef}>
          {imageDims.width > 0 && imageDims.height > 0 ? (
            <Stage
              ref={stageRef}
              width={imageDims.width}
              height={imageDims.height}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
              className="brush-crop__stage"
            >
              {/* Background layer */}
              <Layer>
                {loadedImage ? (
                  <KonvaImage
                    image={loadedImage}
                    x={0}
                    y={0}
                    width={imageDims.width}
                    height={imageDims.height}
                  />
                ) : (
                  <Text x={10} y={20} fill="red" text="Image not loaded" />
                )}
              </Layer>
              {/* Overlay: tinted rectangle and brush strokes */}
              <Layer>
                <Rect
                  x={0}
                  y={0}
                  width={imageDims.width}
                  height={imageDims.height}
                  fill="rgba(0,0,0,0.4)"
                  listening={false}
                />
                {lines.map((line, i) => (
                  <Line
                    key={i}
                    points={line.points}
                    stroke="#fff"
                    strokeWidth={brushStrokeWidth}
                    globalCompositeOperation="destination-out"
                    lineCap="round"
                    lineJoin="round"
                  />
                ))}
              </Layer>
            </Stage>
          ) : (
            <div>Loading image...</div>
          )}
        </div>
      </div>
    );
  }
);

BrushCrop.displayName = 'BrushCrop';
export default BrushCrop;
